import { DateTime } from "luxon";
import { useCallback } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useWeekIndex } from "../contexts/WeekIndexProvider";
import { useRouteWeekStart } from "../contexts/WeekView/RouteWeekContext";
import { getWeekStart } from "../utils/dateUtils";
import { DEFAULT_WEEK_START_DAY } from "../utils/models";
import { toIsoDate } from "../utils/scheduleWeeks";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

export type ShownWeek = {
	/** The first day (Sunday) of the week the staffing pages show; undefined until one is known. */
	weekStart: DateTime | undefined;
	/**
	 * Shows the Sunday week holding `date`. With the schedule cache on that is any week; off, only a week of the old
	 * list, and `false` when the date isn't in it (the shown week then stays as it was). `false` too for an invalid date,
	 * such as a mistyped URL.
	 */
	goToWeek: (date: DateTime) => boolean;
	/** The shown week is the old list's `weekIndex` week: what code still reading the old list by position can show. */
	isLoadedWeek: boolean;
};

/**
 * The week the staffing pages show. With `staffing_schedule_cache` off it is `weekIndex` in the old list, as before.
 * On, it is a date, so it can be any week of the schedule, and the current week until a page names one.
 */
export const useShownWeek = (): ShownWeek => {
	const { weekIndex, setWeekIndex, shownWeekStart, setShownWeekStart } = useWeekIndex();
	const { hospitalData, getWeekIndexByDate, timezone } = useHospitalData();
	const isCacheEnabled = useScheduleCacheEnabled();
	const routeWeekStart = useRouteWeekStart();
	const loadedWeekDay = hospitalData.weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date;
	const loadedWeekStart = loadedWeekDay && getWeekStart(loadedWeekDay, DEFAULT_WEEK_START_DAY);

	const goToWeek = useCallback(
		(date: DateTime): boolean => {
			// An invalid week start would key the schedule queries by nothing, leaving the grid blank with no spinner or error.
			if (!date.isValid) return false;
			const loadedIndex = getWeekIndexByDate(date);
			if (loadedIndex !== -1) setWeekIndex(loadedIndex);
			if (!isCacheEnabled) return loadedIndex !== -1;
			setShownWeekStart(getWeekStart(date, DEFAULT_WEEK_START_DAY));
			return true;
		},
		[getWeekIndexByDate, setWeekIndex, isCacheEnabled, setShownWeekStart],
	);

	// DEFAULT: until a page names a week (the Week view's URL, a date picked, the Day view's date) the cache shows the
	// week the URL names, which the page is about to name, else the current week, where the old list opens; the old
	// list itself never loads with the cache on.
	const weekStart = isCacheEnabled
		? (shownWeekStart ?? routeWeekStart ?? getWeekStart(DateTime.now().setZone(timezone), DEFAULT_WEEK_START_DAY))
		: loadedWeekStart;
	// Compared as calendar dates: the URL's date and the old list's dates can carry different zones.
	const isLoadedWeek = weekStart !== undefined && loadedWeekStart !== undefined && toIsoDate(weekStart) === toIsoDate(loadedWeekStart);
	return { weekStart, goToWeek, isLoadedWeek };
};
